import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import {
  LayoutDashboard, CreditCard, Settings, LogOut, Shield,
  Megaphone, FolderTree, BookOpen, Plus, Edit2, Trash2, Upload,
  QrCode, Save, Loader2, X, Check, Mail, Headphones, Image
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { token, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Notification
  const [notification, setNotification] = useState(null);
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  
  // Active Tab
  const [activeTab, setActiveTab] = useState('pix');
  
  // PIX Config
  const [pixKey, setPixKey] = useState('');
  const [pixQrCode, setPixQrCode] = useState('');
  const [pixQrPreview, setPixQrPreview] = useState('');
  const [pixValue, setPixValue] = useState('');
  const qrInputRef = useRef(null);
  const [savingPix, setSavingPix] = useState(false);
  
  // Support Email
  const [supportEmail, setSupportEmail] = useState('');
  const [savingSupportEmail, setSavingSupportEmail] = useState(false);
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '', content: '', link: '', active: true
  });
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '', icon: 'folder', color: '#ea580c'
  });
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Tutorial
  const [tutorialContent, setTutorialContent] = useState('');
  const [savingTutorial, setSavingTutorial] = useState(false);

  // Banner
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [bannerTitle, setBannerTitle] = useState('');
  const [bannerDescription, setBannerDescription] = useState('');
  const [bannerLink, setBannerLink] = useState('');
  const [savingBanner, setSavingBanner] = useState(false);
  const bannerImageInputRef = useRef(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAll();
  }, [token, navigate]);

  const fetchAll = async () => {
    try {
      const [pixRes, announcementsRes, categoriesRes, tutorialRes, supportRes, bannerRes] = await Promise.all([
        axios.get(`${API}/admin/pix-config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/announcements`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/tutorial`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/support-config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/banner`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPixKey(pixRes.data.pix_key || '');
      setPixQrCode(pixRes.data.pix_qr_code || '');
      setPixQrPreview(pixRes.data.pix_qr_code || '');
      setPixValue(pixRes.data.pix_value || '');
      setAnnouncements(announcementsRes.data);
      setCategories(categoriesRes.data);
      setTutorialContent(tutorialRes.data.content || '');
      setSupportEmail(supportRes.data.support_email || '');
      
      // Banner
      setBannerEnabled(bannerRes.data.enabled || false);
      setBannerImageUrl(bannerRes.data.image_url || '');
      setBannerTitle(bannerRes.data.title || '');
      setBannerDescription(bannerRes.data.description || '');
      setBannerLink(bannerRes.data.link || '');
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  // PIX Functions
  const handleQrUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showNotification('A imagem deve ter no máximo 2MB', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPixQrCode(reader.result);
        setPixQrPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const savePixConfig = async () => {
    setSavingPix(true);
    try {
      await axios.put(
        `${API}/admin/pix-config`,
        { pix_key: pixKey, pix_qr_code: pixQrCode, pix_value: pixValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Configurações PIX salvas!');
    } catch (error) {
      showNotification('Erro ao salvar configurações PIX', 'error');
    } finally {
      setSavingPix(false);
    }
  };

  // Support Email Functions
  const saveSupportEmail = async () => {
    setSavingSupportEmail(true);
    try {
      await axios.put(
        `${API}/admin/support-config`,
        { support_email: supportEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Email de suporte salvo!');
    } catch (error) {
      showNotification('Erro ao salvar email de suporte', 'error');
    } finally {
      setSavingSupportEmail(false);
    }
  };

  // Announcement Functions
  const openAnnouncementModal = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setAnnouncementForm({
        title: announcement.title,
        content: announcement.content,
        link: announcement.link || '',
        active: announcement.active
      });
    } else {
      setEditingAnnouncement(null);
      setAnnouncementForm({ title: '', content: '', link: '', active: true });
    }
    setShowAnnouncementModal(true);
  };

  const saveAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      showNotification('Preencha título e conteúdo', 'error');
      return;
    }
    
    setSavingAnnouncement(true);
    try {
      if (editingAnnouncement) {
        await axios.put(
          `${API}/admin/announcements/${editingAnnouncement.id}`,
          announcementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification('Anúncio atualizado!');
      } else {
        await axios.post(
          `${API}/admin/announcements`,
          announcementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification('Anúncio criado!');
      }
      setShowAnnouncementModal(false);
      fetchAll();
    } catch (error) {
      showNotification('Erro ao salvar anúncio', 'error');
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Excluir este anúncio?')) return;
    
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Anúncio excluído');
      fetchAll();
    } catch (error) {
      showNotification('Erro ao excluir anúncio', 'error');
    }
  };

  // Category Functions
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        icon: category.icon,
        color: category.color
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', icon: 'folder', color: '#ea580c' });
    }
    setShowCategoryModal(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name) {
      showNotification('Preencha o nome da categoria', 'error');
      return;
    }
    
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/admin/categories/${editingCategory.id}`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification('Categoria atualizada!');
      } else {
        await axios.post(
          `${API}/admin/categories`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification('Categoria criada!');
      }
      setShowCategoryModal(false);
      fetchAll();
    } catch (error) {
      showNotification('Erro ao salvar categoria', 'error');
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Categoria excluída');
      fetchAll();
    } catch (error) {
      showNotification('Erro ao excluir categoria', 'error');
    }
  };

  // Tutorial Functions
  const saveTutorial = async () => {
    setSavingTutorial(true);
    try {
      await axios.put(
        `${API}/admin/tutorial`,
        { content: tutorialContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Tutorial atualizado!');
    } catch (error) {
      showNotification('Erro ao salvar tutorial', 'error');
    } finally {
      setSavingTutorial(false);
    }
  };

  // Banner Functions
  const handleBannerImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showNotification('A imagem deve ter no máximo 2MB', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveBanner = async () => {
    setSavingBanner(true);
    try {
      await axios.put(
        `${API}/admin/banner`,
        {
          enabled: bannerEnabled,
          image_url: bannerImageUrl,
          title: bannerTitle,
          description: bannerDescription,
          link: bannerLink
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Banner atualizado!');
    } catch (error) {
      showNotification('Erro ao salvar banner', 'error');
    } finally {
      setSavingBanner(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const iconOptions = [
    { value: 'folder', label: 'Pasta' },
    { value: 'briefcase', label: 'Trabalho' },
    { value: 'user', label: 'Pessoal' },
    { value: 'lightbulb', label: 'Ideia' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {notification.message}
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold">Admin</h1>
              <p className="text-xs text-slate-400">VoiceIdeas</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <Link to="/admin/dashboard" className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            location.pathname === '/admin/dashboard' ? 'bg-slate-800' : 'hover:bg-slate-800'
          }`}>
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </Link>
          <Link to="/admin/pagamentos" className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            location.pathname === '/admin/pagamentos' ? 'bg-slate-800' : 'hover:bg-slate-800'
          }`}>
            <CreditCard className="w-4 h-4" />
            Pagamentos
          </Link>
          <Link to="/admin/configuracoes" className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            location.pathname === '/admin/configuracoes' ? 'bg-slate-800' : 'hover:bg-slate-800'
          }`}>
            <Settings className="w-4 h-4" />
            Configurações
          </Link>
        </nav>
        
        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Configurações</h1>
            <p className="text-slate-500">Gerencie PIX, anúncios, categorias, tutorial e suporte</p>
          </div>

          {/* Tabs */}
          <div className="space-y-6">
            <div className="flex gap-1 p-1 bg-slate-200 rounded-lg w-fit flex-wrap">
              {[
                { id: 'pix', label: 'PIX', icon: QrCode },
                { id: 'banner', label: 'Banner', icon: Image },
                { id: 'announcements', label: 'Anúncios', icon: Megaphone },
                { id: 'categories', label: 'Categorias', icon: FolderTree },
                { id: 'tutorial', label: 'Tutorial', icon: BookOpen },
                { id: 'support', label: 'Suporte', icon: Headphones },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === tab.id 
                      ? 'bg-white shadow text-slate-900' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* PIX Tab */}
            {activeTab === 'pix' && (
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Configurações PIX</h2>
                  <p className="text-sm text-slate-500">Configure o valor, a chave PIX e o QR Code para pagamentos</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Valor da Assinatura (R$)</label>
                  <input
                    type="text"
                    placeholder="Ex: 29,90"
                    value={pixValue}
                    onChange={(e) => setPixValue(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <p className="text-xs text-slate-400">Este valor será exibido na tela de pagamento do usuário</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Chave PIX</label>
                  <input
                    type="text"
                    placeholder="Digite sua chave PIX (CPF, e-mail, telefone ou aleatória)"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">QR Code PIX</label>
                  <input
                    type="file"
                    ref={qrInputRef}
                    onChange={handleQrUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {pixQrPreview ? (
                    <div className="space-y-3">
                      <div className="bg-slate-100 p-4 rounded-xl inline-block">
                        <img 
                          src={pixQrPreview} 
                          alt="QR Code" 
                          className="w-48 h-48 object-contain"
                        />
                      </div>
                      <div>
                        <button
                          onClick={() => qrInputRef.current?.click()}
                          className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50 transition-colors"
                        >
                          Trocar QR Code
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => qrInputRef.current?.click()}
                      className="h-32 w-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-orange-500 hover:text-orange-500 transition-colors"
                    >
                      <Upload className="w-6 h-6" />
                      <span className="text-sm">Upload QR Code</span>
                    </button>
                  )}
                </div>

                <button 
                  onClick={savePixConfig} 
                  disabled={savingPix}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {savingPix ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Configurações
                </button>
              </div>
            )}

            {/* Banner Tab */}
            {activeTab === 'banner' && (
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Banner da Página Inicial</h2>
                  <p className="text-sm text-slate-500">Configure o banner que aparece na página de login/cadastro</p>
                </div>

                {/* Toggle Ativo */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-slate-900">Banner Ativo</h3>
                    <p className="text-sm text-slate-500">Quando ativado, o banner aparece na página inicial</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={bannerEnabled}
                      onChange={(e) => setBannerEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>

                {/* Imagem do Banner */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Imagem do Banner</label>
                  <input
                    type="file"
                    ref={bannerImageInputRef}
                    onChange={handleBannerImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {bannerImageUrl ? (
                    <div className="space-y-3">
                      <div className="bg-slate-100 p-4 rounded-xl">
                        <img 
                          src={bannerImageUrl} 
                          alt="Preview do Banner" 
                          className="max-w-full h-auto max-h-48 object-contain mx-auto rounded-lg"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => bannerImageInputRef.current?.click()}
                          className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50 transition-colors"
                        >
                          Trocar Imagem
                        </button>
                        <button
                          onClick={() => setBannerImageUrl('')}
                          className="px-4 py-2 border border-red-200 text-red-500 rounded-lg text-sm hover:bg-red-50 transition-colors"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => bannerImageInputRef.current?.click()}
                      className="h-32 w-full border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-orange-500 hover:text-orange-500 transition-colors"
                    >
                      <Upload className="w-6 h-6" />
                      <span className="text-sm">Upload da Imagem do Banner</span>
                      <span className="text-xs">Recomendado: 800x200px, máx 2MB</span>
                    </button>
                  )}
                </div>

                {/* Título */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Título</label>
                  <input
                    type="text"
                    placeholder="Ex: Promoção Especial!"
                    value={bannerTitle}
                    onChange={(e) => setBannerTitle(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>

                {/* Descrição */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Descrição</label>
                  <textarea
                    placeholder="Ex: Aproveite 50% de desconto no primeiro mês!"
                    value={bannerDescription}
                    onChange={(e) => setBannerDescription(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none h-20"
                  />
                </div>

                {/* Link */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Link (opcional)</label>
                  <input
                    type="text"
                    placeholder="https://..."
                    value={bannerLink}
                    onChange={(e) => setBannerLink(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <p className="text-xs text-slate-400">Se preenchido, o banner será clicável</p>
                </div>

                {/* Preview */}
                {(bannerTitle || bannerDescription || bannerImageUrl) && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Preview</label>
                    <div className="border-2 border-dashed border-orange-200 rounded-xl p-4 bg-orange-50">
                      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        {bannerImageUrl && (
                          <img src={bannerImageUrl} alt="Banner" className="w-full h-32 object-cover" />
                        )}
                        <div className="p-4">
                          {bannerTitle && <h3 className="font-bold text-slate-900">{bannerTitle}</h3>}
                          {bannerDescription && <p className="text-sm text-slate-600 mt-1">{bannerDescription}</p>}
                          {bannerLink && (
                            <p className="text-xs text-orange-500 mt-2">Clique para acessar →</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button 
                  onClick={saveBanner} 
                  disabled={savingBanner}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {savingBanner ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Banner
                </button>
              </div>
            )}

            {/* Announcements Tab */}
            {activeTab === 'announcements' && (
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Anúncios</h2>
                    <p className="text-sm text-slate-500">Gerencie os banners exibidos aos usuários</p>
                  </div>
                  <button 
                    onClick={() => openAnnouncementModal()}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Novo Anúncio
                  </button>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-auto">
                  {announcements.map((ann) => (
                    <div 
                      key={ann.id} 
                      className="p-4 border rounded-xl flex items-start justify-between gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-900">{ann.title}</h4>
                          {ann.active ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              Ativo
                            </span>
                          ) : (
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                              Inativo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{ann.content}</p>
                        {ann.link && (
                          <a href={ann.link} target="_blank" rel="noopener noreferrer" 
                            className="text-xs text-orange-500 hover:underline"
                          >
                            {ann.link}
                          </a>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => openAnnouncementModal(ann)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </button>
                        <button 
                          onClick={() => deleteAnnouncement(ann.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {announcements.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      Nenhum anúncio cadastrado
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Categorias</h2>
                    <p className="text-sm text-slate-500">Gerencie as categorias do Banco de Ideias</p>
                  </div>
                  <button 
                    onClick={() => openCategoryModal()}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Nova Categoria
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {categories.map((cat) => (
                    <div 
                      key={cat.id}
                      className="p-4 border rounded-xl flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${cat.color}20` }}
                        >
                          <FolderTree className="w-5 h-5" style={{ color: cat.color }} />
                        </div>
                        <span className="font-medium text-slate-900">{cat.name}</span>
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => openCategoryModal(cat)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </button>
                        <button 
                          onClick={() => deleteCategory(cat.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tutorial Tab */}
            {activeTab === 'tutorial' && (
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Tutorial</h2>
                  <p className="text-sm text-slate-500">Edite o conteúdo do tutorial exibido aos usuários</p>
                </div>

                <textarea
                  placeholder="Digite o conteúdo do tutorial aqui..."
                  value={tutorialContent}
                  onChange={(e) => setTutorialContent(e.target.value)}
                  className="w-full min-h-[300px] px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none"
                />
                
                <button 
                  onClick={saveTutorial} 
                  disabled={savingTutorial}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {savingTutorial ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Tutorial
                </button>
              </div>
            )}

            {/* Support Tab */}
            {activeTab === 'support' && (
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Email de Suporte</h2>
                  <p className="text-sm text-slate-500">Configure o email que os usuários verão para entrar em contato</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Email para contato</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        placeholder="suporte@seudominio.com"
                        value={supportEmail}
                        onChange={(e) => setSupportEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400">Este email será exibido para os usuários na área de suporte</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Como funciona:</strong> Os usuários verão um botão "Suporte" com seu email. 
                    Ao clicar, abrirá o aplicativo de email deles para enviar mensagem diretamente para você.
                  </p>
                </div>
                
                <button 
                  onClick={saveSupportEmail} 
                  disabled={savingSupportEmail}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {savingSupportEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Email de Suporte
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-lg text-slate-900">
                  {editingAnnouncement ? 'Editar Anúncio' : 'Novo Anúncio'}
                </h3>
                <p className="text-sm text-slate-500">Preencha os dados do anúncio</p>
              </div>
              <button 
                onClick={() => setShowAnnouncementModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Título</label>
                <input
                  type="text"
                  placeholder="Título do anúncio"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Conteúdo</label>
                <textarea
                  placeholder="Texto do anúncio"
                  value={announcementForm.content}
                  onChange={(e) => setAnnouncementForm(f => ({ ...f, content: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none h-24"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Link (opcional)</label>
                <input
                  type="text"
                  placeholder="https://..."
                  value={announcementForm.link}
                  onChange={(e) => setAnnouncementForm(f => ({ ...f, link: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Ativo</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={announcementForm.active}
                    onChange={(e) => setAnnouncementForm(f => ({ ...f, active: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button 
                onClick={() => setShowAnnouncementModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={saveAnnouncement} 
                disabled={savingAnnouncement}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {savingAnnouncement && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-lg text-slate-900">
                  {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
                </h3>
                <p className="text-sm text-slate-500">Preencha os dados da categoria</p>
              </div>
              <button 
                onClick={() => setShowCategoryModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Nome</label>
                <input
                  type="text"
                  placeholder="Nome da categoria"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Ícone</label>
                <select
                  value={categoryForm.icon}
                  onChange={(e) => setCategoryForm(f => ({ ...f, icon: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Cor</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm(f => ({ ...f, color: e.target.value }))}
                    className="w-14 h-10 p-1 cursor-pointer border rounded-lg"
                  />
                  <input
                    type="text"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm(f => ({ ...f, color: e.target.value }))}
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button 
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={saveCategory} 
                disabled={savingCategory}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {savingCategory && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
